import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileUpload, FileUploadChunk, FileUploadCompleted, FileUploadEvent } from '../models/file-upload.model';
import { FileDetails } from '../models/file-details.model';
import { Folder } from '../models/folder.model';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly httpClient = inject(HttpClient);

  public createFolder(folder: Folder): Observable<Folder> {
    return this.httpClient.post<Folder>(`${environment.baseUrl}/folders`, folder);
  }

  public getFolder(folderId: string): Observable<FileDetails> {
    return this.httpClient.get<FileDetails>(`${environment.baseUrl}/folders/${folderId}`);
  }

  public createFile(folderId: string, size: number): Observable<FileUpload> {
    return this.httpClient.post<FileUpload>(`${environment.baseUrl}/folders/${folderId}/files/${size}`, { });
  }

  public uploadFile(fileUploadChunk: FileUploadChunk, file: FileDetails | Blob): Observable<FileUploadEvent> {
    return this.httpClient
      .put<unknown>(fileUploadChunk.url, file, { reportProgress: true, observe: 'events' }, )
      .pipe(
        map((event: HttpEvent<unknown>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              return {
                bytesUploaded: 0,
                bytesTotal: (fileUploadChunk.end - fileUploadChunk.start),
                progress: 0,
                completed: false,
              };
            case HttpEventType.UploadProgress:
              return {
                bytesUploaded: event.loaded,
                bytesTotal: event.total,
                progress: event.total ? Math.round((100 * event.loaded) / event.total) : 0,
                completed: false,
              };
            case HttpEventType.Response:
              return {
                bytesUploaded: (fileUploadChunk.end - fileUploadChunk.start),
                bytesTotal: (fileUploadChunk.end - fileUploadChunk.start),
                progress: 100,
                completed: true,
              };
            default:
              return null;
          }
        }),
      );
  }

  public createFileCompleted(folderId: string, fileUploadCompleted: FileUploadCompleted): Observable<FileDetails> {
    return this.httpClient.put<FileDetails>(`${environment.baseUrl}/folders/${folderId}/files/completed`, fileUploadCompleted);
  }

  public findFiles(folderId: string): Observable<FileDetails[]> {
    return this.httpClient.get<FileDetails[]>(`${environment.baseUrl}/folders/${folderId}/files`, { withCredentials: true });
  }

  public deleteFile(file: FileDetails) {
    return this.httpClient.delete<boolean>(`${environment.baseUrl}/folders/${file.parent.id}/files/${file.id}`, { withCredentials: true });
  }

  public downloadFile(folderId: string, fileId: string): Observable<Blob> {
    return this.httpClient.get(
      `${environment.baseUrl}/folders/${folderId}/files/${fileId}/download`,
      {
        responseType: 'blob',
        withCredentials: true
      }
    );
  }

  public downloadAll(folderId: string): Observable<Blob> {
    return this.httpClient.get(
      `${environment.baseUrl}/folders/${folderId}/download`,
      {
        responseType: 'blob',
        withCredentials: true
      }
    );
  }

  public triggerBrowserDownload(blob: Blob, downloadFileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  public openFilePreview(folderId: string, fileId: string) {
    this.httpClient.get<any>(`${environment.baseUrl}/folders/${folderId}/files/${fileId}/preview`, { withCredentials: true })
      .subscribe((response) => {
        const byteCharacters = atob(response.contentBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.contentType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      });
  }
}
